import { Injectable, Inject, OnDestroy } from '@angular/core';
import {
  ResolveEnd,
  ActivatedRouteSnapshot,
  RouterEvent
} from '@angular/router';
import { ApplicationInsights, IPageViewTelemetry, IEventTelemetry, IExceptionTelemetry, ITraceTelemetry, SeverityLevel  } from '@microsoft/applicationinsights-web';

import { LoggingService } from '@core/services/logging.service';
import { LogLevel } from '@shared/enums/LogLevel';
import { User } from '@core/redux/stores';
import { select } from '@angular-redux/store';
import { Observable ,  Subscription } from 'rxjs';

@Injectable()
export class AppinsightsLoggingService extends LoggingService
  implements OnDestroy {
  @select(appState => appState.user)
  userObject: Observable<User>;
  user: User;
  private subscriptions = new Subscription();
  appInsights: ApplicationInsights;
  constructor(
    @Inject('AppInsightsConfig') config: any,
    @Inject('LogLevel') private level: LogLevel
  ) {
    super();
    this.appInsights = new ApplicationInsights(config);
    if (this.appInsights) {
      this.appInsights.loadAppInsights();
    }
    this.subscriptions.add(
      this.userObject.subscribe(response => {
        this.user = <User>response;
      })
    );
  }
  logPageViewWithEvent(event: RouterEvent) {
    if (event instanceof ResolveEnd) {
      const activatedComponent = this.getActivatedComponent(event.state.root);
      if (activatedComponent) {
        this.logPageView(
          activatedComponent.name,
          this.getRouteTemplate(event.state.root)
        );
      }
    }
  }
  setAuthenticatedUserContext(
    userId: string,
    userType?: string,
    storeInCookie?: boolean
  ): void {
    if (!storeInCookie) {
      storeInCookie = false;
    }
    this.appInsights.setAuthenticatedUserContext(userId, userType);
  }
  clearAuthenticatedUserContext(): void {
    this.appInsights.clearAuthenticatedUserContext();
  }
  private getActivatedComponent(snapshot: ActivatedRouteSnapshot): any {
    if (snapshot.firstChild) {
      return this.getActivatedComponent(snapshot.firstChild);
    }

    return snapshot.component;
  }

  private getRouteTemplate(snapshot: ActivatedRouteSnapshot): string {
    let path = '';
    if (snapshot.routeConfig) {
      path += snapshot.routeConfig.path;
    }

    if (snapshot.firstChild) {
      return path + this.getRouteTemplate(snapshot.firstChild);
    }

    return path;
  }

  private AddGlobalProperties(properties?: {
    [key: string]: string;
  }): { [key: string]: string } {
    if (!properties || properties === null || typeof(properties) !== 'object') {
      properties = {};
    }
    if (typeof properties === 'string') {
      const message = properties;
      properties = {};
      properties['message'] = message;
    }
    // add your custom properties such as app version
    if (this.user && this.user.email) {
      properties['email'] = this.user.email;
    }
    if (this.user && this.user.id) {
      properties['userId'] = this.user.id;
    }
    return properties;
  }

  private logPageView(
    name: string,
    url?: string,
    properties?: { [key: string]: string },
    measurements?: { [key: string]: number },
    duration?: number
  ) {
    const pageViewTelemetry: IPageViewTelemetry = {
      isLoggedIn: this.user !== null && this.user !== undefined,
      name: name,
      uri: url,
      properties: this.AddGlobalProperties()
    };
    this.appInsights.trackPageView(pageViewTelemetry);
  }

  public logEvent(
    name: string,
    properties?: { [key: string]: string },
    measurements?: { [key: string]: number }
  ) {
    const eventTelemetry: IEventTelemetry = {
      name: name,
      properties: properties
    };
    this.appInsights.trackEvent(
      eventTelemetry,
      this.AddGlobalProperties(properties),
    );
  }

  public logError(
    error: Error,
    properties?: { [key: string]: string },
    measurements?: { [key: string]: number }
  ) {
    const exceptionTelemetry: IExceptionTelemetry = {
      error: error,
      exception: error,
      properties: properties
    };
    this.appInsights.trackException(exceptionTelemetry);
  }
  public log(
    message: string,
    properties?: { [key: string]: string },
    logLevel?: LogLevel
  ) {
    if (!logLevel) {
      logLevel = this.level;
    }
    const traceTelemetry: ITraceTelemetry = {
     message: message,
     properties: properties,
     severityLevel: SeverityLevel[LogLevel[logLevel]]
    };
    this.appInsights.trackTrace(traceTelemetry);
  }
  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
